// Sidebar.tsx
import { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import {
  Paper,
  Box,
  Chip,
  Typography,
  SxProps,
  Theme,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  CalendarMonth,
  LocationOn,
  North,
  Person,
  PeopleAlt,
} from "@mui/icons-material";

import apiService from "../../../service/Api";

import { getTotemsImages, TotemDetails } from "../../../utils/GetTotems";

import { BookingResponse } from "../../bookings/types/view-booking";
import type { IThemeState } from "../../../types/theme";
import PropTypes from "prop-types";

// ---- Overview Interface ----
interface OverviewResponse {
  deceased: {
    id: number;
    full_name: string;
    relationship: string | null;
    date_of_birth: string;
    date_of_death: string;
    passed_in_city: string | null;
    passed_in_country: string | null;
  };
  website: {
    id: number;
    theme: {
      name: string;
      thumbnail: string | null;
    };
    visibility: string;
    allow_comments: boolean;
    allow_tributes: boolean;
  };
  metrics: {
    total_visitors: number;
    total_comments: number;
    total_reach: number;
    total_engagements: number;
  };
}

// Theme type
interface ThemeRead {
  id: number;
  name: string;
  color: string;
  image?: string | null;
  thumbnail?: string | null;
}

// --- Cover Card ---
interface CoverCardProps {
  overview: OverviewResponse | null;
  sx?: SxProps<Theme>;
  bookingData: BookingResponse | null;
}

export const CoverCard: React.FC<CoverCardProps> = ({
  overview,
  bookingData,
}) => {
  const muiTheme = useTheme();
  const themeColors = tokens(muiTheme.palette.mode);
  const [theme, setTheme] = useState<ThemeRead | null>(null);
  const [loading] = useState(false);
  const [totemName, setTotemName] = useState<string | null>(null); // totem state

  const deceasedId = bookingData?.deceased?.[0]?.id;

  const fetchWebsite = useCallback(async () => {
    if (!deceasedId) return;

    try {
      const res = await apiService.get(
        `/api/client-portal/websites/?deceased=${deceasedId}`
      );
      const dataResponse = res.data as Array<{
        theme?: ThemeRead;
        deceased?: { totem?: string | null };
      }>;

      // grab theme and totem from the nested deceased object
      setTheme(dataResponse[0]?.theme ?? null);
      setTotemName(dataResponse[0]?.deceased?.totem || null);
    } catch (err) {
      console.error("Failed to fetch website info", err);
    }
  }, [deceasedId]);

  useEffect(() => {
    fetchWebsite();
  }, [fetchWebsite]);

  const isActive = bookingData?.company?.is_active ?? false;

  // get all totems
  const totemsList: TotemDetails[] = getTotemsImages();

  const matchedTotem = totemsList.find(
    (t) => t.name.toLowerCase() === (totemName || "").toLowerCase()
  );

  return (
    <Paper
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "228px",
        borderRadius: 3,
        fontWeight: "bold",
        boxShadow: 0,
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          backgroundColor: themeColors.primary[800],
        }}
      >
        {/* Theme Image / Placeholder */}
        {loading ? (
          <Box
            sx={{
              height: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: themeColors.primary[800],
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : theme?.image ? (
          <Box
            component="img"
            src={theme.image}
            alt={theme.name}
            sx={{
              width: "100%",
              height: 150,
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : theme?.thumbnail ? (
          <Box
            component="img"
            src={theme.thumbnail}
            alt={theme.name}
            sx={{
              width: "100%",
              height: 150,
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            component="img"
            src={
              "https://client.muzukuru.com/static/media/defaultImage.5e2b5b3849cfff922cf0.png"
            }
            alt={"default theme"}
            sx={{
              width: "100%",
              height: 150,
              objectFit: "cover",
              display: "block",
            }}
          />
        )}

        {/* Text on image */}
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            left: 16,
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            color: "#fff",
            textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
          }}
        >
          <Box sx={{ fontSize: 16 }}>In Remembrance of</Box>
          <Box sx={{ fontSize: 16, fontWeight: 700, mt: 1 }}>
            {overview?.deceased?.full_name}
          </Box>
          <Box sx={{ fontSize: 12, mt: 0.5 }}>
            {overview?.deceased?.date_of_birth &&
              overview?.deceased?.date_of_death && (
                <>
                  {new Date(overview.deceased.date_of_birth).getFullYear()} –{" "}
                  {new Date(overview.deceased.date_of_death).getFullYear()}
                </>
              )}
          </Box>
        </Box>

        {/* Deceased Image Placeholder */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 6,
            transform: "translateY(-50%)",
            width: 100,
            height: 100,
            borderRadius: "15%",
            border: "4px solid #fff",
            boxShadow: 2,
            overflow: "visible",
            backgroundColor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Render deceased image */}
          {bookingData?.deceased?.[0]?.person_image && (
            <Box
              component="img"
              src={bookingData.deceased[0].person_image}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "inherit",
              }}
            />
          )}

          {matchedTotem && (
            <Box
              sx={{
                position: "absolute",
                left: -20,
                top: "100%",
                transform: "translateY(-50%)",
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "2px solid #fff",
                boxShadow: 1,
                bgcolor: "#9c9b9bf1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                zIndex: 10,
              }}
            >
              <Box
                component="img"
                src={matchedTotem.img}
                alt={matchedTotem.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Active status */}
      <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}>
        <Chip
          label={isActive ? "Active" : "Inactive"}
          size="small"
          sx={{
            px: 1,
            color: isActive ? "success.main" : "error.main",
            bgcolor: isActive ? "#D8FBF3" : "#FBD8D8",
            fontWeight: 600,
            borderRadius: 1.5,
          }}
        />

        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.8rem", color: "text.secondary", opacity: 0.6 }}
        >
          {bookingData?.updated_at
            ? `Last updated ${formatDistanceToNow(
                new Date(bookingData.updated_at),
                { addSuffix: true }
              )}`
            : "Last updated recently"}
        </Typography>
      </Box>
    </Paper>
  );
};

// Client Info Card
export const ClientInfoCard: React.FC<{
  overview: OverviewResponse | null;
  bookingData: BookingResponse;
}> = ({ overview, bookingData }) => {
  const deceased = overview?.deceased;
  return (
    <Paper
      sx={{
        p: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "250px",
        borderRadius: 3,
        fontWeight: "bold",
        boxShadow: 0,
      }}
    >
      Client Information
      <Typography
        variant="subtitle1"
        sx={{ mt: 1, fontSize: "0.8rem", color: "text.secondary" }}
      >
        <Person fontSize="small" /> {bookingData?.created_by?.first_name}{" "}
        {bookingData?.created_by?.last_name}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "text.secondary" }}>
        <PeopleAlt fontSize="small" /> {deceased?.relationship || "N/A"}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "text.secondary" }}>
        <CalendarMonth fontSize="small" />{" "}
        {bookingData?.created_at
          ? new Date(bookingData.created_at).toLocaleDateString()
          : "N/A"}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "text.secondary" }}>
        <LocationOn fontSize="small" /> {deceased?.passed_in_country || "N/A"}
      </Typography>
    </Paper>
  );
};

// STAT Component
const Stat = ({
  value,
  label,
  delta,
  deltaColor = "success.main",
}: {
  value: string | number;
  label: string;
  delta: string;
  deltaColor?: string;
}) => (
  <Paper
    sx={{
      p: 1.2,
      height: "75px",
      borderRadius: 2,
      backgroundColor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 6,
        right: 6,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
        color: deltaColor,
      }}
    >
      <North sx={{ fontSize: "12px" }} />
      <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
        {delta}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{value}</Typography>
    <Typography
      sx={{ fontSize: "11px", color: "text.secondary", opacity: 0.7, mt: 0.5 }}
    >
      {label}
    </Typography>
  </Paper>
);

// Page Stats Card
export const PageStatsCard = ({
  overview,
}: {
  overview: OverviewResponse | null;
}) => {
  if (!overview)
    return (
      <Paper sx={{ p: 2, height: "240px", borderRadius: 3, boxShadow: 0 }}>
        Loading...
      </Paper>
    );

  const metrics = overview.metrics;

  return (
    <Paper
      sx={{
        p: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "240px",
        borderRadius: 3,
        fontWeight: "bold",
      }}
    >
      Page statistics
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gridAutoRows: "1fr",
          gap: 1.5,
          flexGrow: 1,
          mt: 1.5,
        }}
      >
        <Stat
          value={metrics.total_visitors}
          label="Total Visitors"
          delta="-%"
        />
        <Stat
          value={metrics.total_engagements}
          label="Engagements"
          delta="-%"
        />
        <Stat value={metrics.total_reach} label="Reach" delta="-%" />
        <Stat
          value={metrics.total_comments}
          label="Comments"
          delta="-%"
          deltaColor="error.main"
        />
      </Box>
    </Paper>
  );
};

// Actions Card
export const ActionsCard = ({
  overview,
}: {
  overview: OverviewResponse | null;
}) => (
  <Paper
    sx={{
      p: 2,
      width: "100%",
      borderRadius: 3,
      fontWeight: "bold",
      height: "180px",
      boxShadow: 0,
    }}
  >
    Client Actions
    <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.8rem" }}>
      Send a request for proposals
    </Typography>
    <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.8rem" }}>
      Approved comments {""}
      {overview?.metrics.total_comments}
    </Typography>
  </Paper>
);

// Sidebar
export const Sidebar: React.FC<{
  reduxTheme: IThemeState;
  deceased_id: string;
  bookingData: BookingResponse | null;
}> = ({ reduxTheme: _reduxTheme, deceased_id, bookingData }) => {
  void _reduxTheme;
  const [overview, setOverview] = useState<OverviewResponse | null>(null);

  // Fetch overview
  useEffect(() => {
    const fetchOverview = async () => {
      if (!deceased_id) return;
      try {
        const response = await apiService.get<OverviewResponse>(
          `/api/individuals/deceased/${deceased_id}/overview/`
        );
        setOverview(response.data);
      } catch (error) {
        console.error("Failed to fetch overview:", error);
      }
    };
    fetchOverview();
  }, [deceased_id]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      width="340px"
      height="850px"
      alignItems="stretch"
    >
      {bookingData && (
        <CoverCard overview={overview} bookingData={bookingData} />
      )}
      {bookingData && (
        <ClientInfoCard overview={overview} bookingData={bookingData} />
      )}

      <PageStatsCard overview={overview} />
      <ActionsCard overview={overview} />
    </Box>
  );
};

const overviewShape = PropTypes.shape({
  deceased: PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    relationship: PropTypes.string,
    date_of_birth: PropTypes.string,
    date_of_death: PropTypes.string,
    passed_in_city: PropTypes.string,
    passed_in_country: PropTypes.string,
  }),
  website: PropTypes.shape({
    id: PropTypes.number,
    theme: PropTypes.shape({
      name: PropTypes.string,
      thumbnail: PropTypes.string,
    }),
    visibility: PropTypes.string,
    allow_comments: PropTypes.bool,
    allow_tributes: PropTypes.bool,
  }),
  metrics: PropTypes.shape({
    total_visitors: PropTypes.number,
    total_comments: PropTypes.number,
    total_reach: PropTypes.number,
    total_engagements: PropTypes.number,
  }),
});

const bookingDataShape = PropTypes.shape({
  deceased: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      person_image: PropTypes.string,
    })
  ),
  company: PropTypes.shape({
    is_active: PropTypes.bool,
  }),
  updated_at: PropTypes.string,
  created_at: PropTypes.string,
  created_by: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
});

CoverCard.propTypes = {
  overview: overviewShape,
  bookingData: bookingDataShape,
};

ClientInfoCard.propTypes = {
  overview: overviewShape,
  bookingData: bookingDataShape,
};

PageStatsCard.propTypes = {
  overview: overviewShape,
};

ActionsCard.propTypes = {
  overview: overviewShape,
};

Sidebar.propTypes = {
  reduxTheme: PropTypes.shape({
    primary_colors: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  deceased_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  bookingData: bookingDataShape,
};

export default Sidebar;
